import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { affiliationAPI, orgAPI } from '../../services/api';
import { Loader2, RefreshCw, Download, BellRing, AlertTriangle, CalendarClock } from 'lucide-react';

const VIGENCIA_BADGE = {
  vigente: 'bg-green-100 text-green-800',
  por_vencer: 'bg-amber-100 text-amber-800',
  vencido: 'bg-red-100 text-red-800',
  sin_vigencia: 'bg-gray-100 text-gray-700',
};
const PAGO_BADGE = {
  completed: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-700',
};

const fecha = (v) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');
const dinero = (v, m) => (v == null ? '—' : `$${Number(v).toLocaleString('es-MX')} ${m || ''}`);

// Panel territorial de renovaciones y conciliación. QRO ve sólo QRO; Nacional consolida.
const AdminRenovaciones = () => {
  const [secciones, setSecciones] = useState([]);
  const [seccion, setSeccion] = useState('');
  const [renov, setRenov] = useState(null);
  const [pagos, setPagos] = useState(null);
  const [avisos, setAvisos] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [cargando, setCargando] = useState(true);
  const { toast } = useToast();

  const err = (e) => toast({ title: 'Operación rechazada', description: e.message, variant: 'destructive' });

  const cargar = async (sec) => {
    setCargando(true);
    try {
      const [r, p] = await Promise.all([
        affiliationAPI.renovaciones(sec || undefined),
        affiliationAPI.pagos(sec || undefined),
      ]);
      setRenov(r);
      setPagos(p);
    } catch (e) { err(e); } finally { setCargando(false); }
  };

  useEffect(() => {
    orgAPI.getSections().then((s) => setSecciones(s || [])).catch(() => setSecciones([]));
    cargar('');
  }, []);

  const calcularAvisos = async (dryRun) => {
    try {
      const a = await affiliationAPI.avisosRenovacion(dryRun, seccion || undefined);
      setAvisos(a);
      toast({
        title: dryRun ? 'Simulación de avisos' : 'Avisos registrados',
        description: `${a.total_programados} programados · ${a.registrados} nuevos · sin envío real de correos`,
      });
      if (!dryRun) cargar(seccion);
    } catch (e) { err(e); }
  };

  const renovar = async (item) => {
    try {
      const res = await affiliationAPI.iniciarRenovacion(item.request_id, motivo);
      toast({
        title: `Renovación abierta · ${res.folio}`,
        description: `Expediente conservado (${res.documentos_conservados} documentos). Cobra la cuota y activa tras el webhook.`,
      });
      setMotivo('');
      cargar(seccion);
    } catch (e) { err(e); }
  };

  const exportar = async () => {
    try {
      const { blob, registros } = await affiliationAPI.descargarPagosCsv(seccion || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conciliacion_upim_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'CSV exportado', description: `${registros || 0} registros de tu alcance territorial` });
    } catch (e) { err(e); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-renovaciones">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Renovaciones y conciliación</h1>
            <p className="text-sm text-gray-600">
              Vigencia anual, avisos sin envío real y conciliación de pagos en modo TEST.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select className="border rounded-md px-3 py-2 text-sm" value={seccion}
                    data-testid="filtro-seccion"
                    onChange={(e) => { setSeccion(e.target.value); cargar(e.target.value); }}>
              <option value="">Mi alcance territorial</option>
              {secciones.map((s) => (
                <option key={s.id} value={s.id}>{s.abbreviation} — {s.name}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={() => cargar(seccion)} data-testid="btn-recargar">
              <RefreshCw className="h-4 w-4 mr-2" /> Recargar
            </Button>
            <Button variant="outline" size="sm" onClick={exportar} data-testid="btn-exportar-csv">
              <Download className="h-4 w-4 mr-2" /> Exportar CSV
            </Button>
          </div>
        </div>

        {cargando && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>}

        {!cargando && renov && (
          <Card data-testid="tarjeta-renovaciones">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarClock className="h-4 w-4" /> Por renovar
                <Badge className="bg-amber-100 text-amber-800" data-testid="conteo-por-vencer">
                  {renov.resumen?.por_vencer || 0} por vencer
                </Badge>
                <Badge className="bg-red-100 text-red-800" data-testid="conteo-vencidos">
                  {renov.resumen?.vencido || 0} vencidos
                </Badge>
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => calcularAvisos(true)} data-testid="btn-simular-avisos">
                  <BellRing className="h-4 w-4 mr-2" /> Simular avisos
                </Button>
                <Button size="sm" onClick={() => calcularAvisos(false)} data-testid="btn-registrar-avisos">
                  Registrar avisos
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-gray-500 mb-3">
                Ventana de aviso: {renov.ventana_por_vencer_dias} días · hitos {(renov.hitos_aviso_dias || []).join('/')} días ·
                cuota vigente {dinero(renov.cuota_vigente, 'MXN')} · los avisos NO envían correos reales.
              </p>
              <Input placeholder="Motivo opcional de la renovación" value={motivo} className="mb-3 max-w-md"
                     onChange={(e) => setMotivo(e.target.value)} data-testid="input-motivo-renovacion" />
              {(renov.items || []).length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="sin-renovaciones">
                  No hay afiliados por vencer ni vencidos en tu alcance.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-500">
                      <tr><th className="py-2">Folio</th><th>Afiliado</th><th>Vigencia</th>
                        <th>Días</th><th>Estado</th><th>Aviso</th><th></th></tr>
                    </thead>
                    <tbody>
                      {renov.items.map((i) => (
                        <tr key={i.request_id} className="border-t" data-testid={`fila-renovacion-${i.folio}`}>
                          <td className="py-2 font-medium">{i.folio}</td>
                          <td>{i.nombre}</td>
                          <td>{fecha(i.vigencia_hasta)}</td>
                          <td data-testid={`dias-${i.folio}`}>{i.dias_para_vencer}</td>
                          <td>
                            <Badge className={VIGENCIA_BADGE[i.vigencia_estado] || ''}>
                              {i.vigencia_estado === 'por_vencer' ? 'por vencer' : i.vigencia_estado}
                            </Badge>
                          </td>
                          <td className="text-xs text-gray-500">
                            {i.hito_aplicable ? `${i.hito_aplicable} días` : '—'}
                            {i.avisos_registrados?.length ? ` · ${i.avisos_registrados.length} enviados` : ''}
                          </td>
                          <td className="text-right">
                            <Button size="sm" variant="outline" onClick={() => renovar(i)}
                                    data-testid={`btn-renovar-${i.folio}`}>
                              Abrir renovación
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {avisos && (
          <Card data-testid="tarjeta-avisos">
            <CardHeader><CardTitle className="text-base">
              Avisos {avisos.dry_run ? '(simulación)' : '(registrados en modo mock)'}
            </CardTitle></CardHeader>
            <CardContent className="text-sm text-gray-700">
              {avisos.total_programados} programados · {avisos.registrados} nuevos ·
              envío real de correos: <strong>no</strong>
            </CardContent>
          </Card>
        )}

        {!cargando && pagos && (
          <Card data-testid="tarjeta-conciliacion">
            <CardHeader><CardTitle className="text-base flex items-center gap-2">
              Conciliación de pagos ({pagos.modo})
              <Badge className="bg-green-100 text-green-800" data-testid="total-confirmado">
                {dinero(pagos.total_confirmado, pagos.moneda)} confirmado
              </Badge>
              {pagos.en_revision > 0 && (
                <Badge className="bg-amber-100 text-amber-800" data-testid="conteo-revision">
                  <AlertTriangle className="h-3 w-3 mr-1" /> {pagos.en_revision} en revisión
                </Badge>
              )}
            </CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-500">
                    <tr><th className="py-2">Folio</th><th>Afiliado</th><th>Ciclo</th><th>Importe</th>
                      <th>Estado</th><th>Fecha</th><th>Evento Stripe</th><th>Discrepancia</th></tr>
                  </thead>
                  <tbody>
                    {(pagos.items || []).map((p) => (
                      <tr key={p.id} className="border-t" data-testid={`fila-pago-${p.folio}`}>
                        <td className="py-2 font-medium">{p.folio}</td>
                        <td>{p.solicitante}</td>
                        <td className="text-xs">{p.ciclo}</td>
                        <td>{dinero(p.amount, p.currency)}</td>
                        <td><Badge className={PAGO_BADGE[p.estado] || ''}>{p.estado}</Badge></td>
                        <td>{fecha(p.confirmado_at || p.fecha)}</td>
                        <td className="text-xs text-gray-500">
                          {p.stripe_event_id ? `${p.stripe_event_id.slice(0, 14)}… (${p.eventos_procesados})` : '—'}
                        </td>
                        <td className="text-xs">
                          {p.discrepancia
                            ? <span className="text-amber-700" data-testid={`discrepancia-${p.folio}`}>
                                {p.discrepancia.tipo}
                              </span>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(pagos.items || []).length === 0 && (
                <p className="text-sm text-gray-500" data-testid="sin-pagos">Sin pagos en tu alcance territorial.</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRenovaciones;
